import React from 'react'
import * as Sentry from '@sentry/react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { beforeSend } from './sentry-beforesend'

// __PG19_SENTRY_RELEASE__ подставляется vite `define` в vite.config.ts —
// единая строка и для runtime init, и для sourcemap upload. Без этого случается
// release drift: runtime шлёт 'unreleased', а plugin грузит под <gitSha>.
declare const __PG19_SENTRY_RELEASE__: string

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENABLED = !!DSN

const apiOrigin =
  (import.meta.env.VITE_SENTRY_API_ORIGIN as string | undefined) ||
  (import.meta.env.DEV ? 'http://localhost:4300' : 'https://core.pg19.ru')

// v2: Session Replay feature flag — default OFF для kill switch без code change.
// Выставляется VITE_SENTRY_REPLAY_ENABLED=true в .env.production на deploy.
// Rollback при PII leak / disk exhaustion: выставить false + redeploy (B.6 design doc).
const REPLAY_ENABLED =
  ENABLED && (import.meta.env.VITE_SENTRY_REPLAY_ENABLED as string | undefined) === 'true'

// v2: session_id tag для pre-auth attribution. До того как useAuthInit вызывает
// Sentry.setUser({id}), события оседают с session_id. Клиент копирует id с error
// screen (RouteErrorBoundary) и отдаёт саппорту — support UI найдёт события
// по tags.session_id даже без user.id.
const SESSION_ID_KEY = 'sentry_session_id'
function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY)
    if (existing) return existing
    // crypto.randomUUID — built-in, 128-bit. Slice до 8 char для display.
    const id = (globalThis.crypto?.randomUUID?.() ?? String(Date.now())).replace(/-/g, '').slice(0, 8)
    sessionStorage.setItem(SESSION_ID_KEY, id)
    return id
  } catch {
    // private-mode Safari / iframe без storage access — fallback на runtime-only id
    return String(Date.now()).slice(-8)
  }
}
const sessionId = getOrCreateSessionId()

Sentry.init({
  dsn: DSN,
  enabled: ENABLED,
  // VITE_SENTRY_ENVIRONMENT позволяет разделять deployment environment
  // (development staging / production) от vite build mode. Dev staging
  // всегда собирается с mode=production (для bundle/PWA perf), так что
  // MODE как env — ошибка, обычный fallback.
  environment:
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    import.meta.env.MODE,
  release: __PG19_SENTRY_RELEASE__,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '1.0'),
  // src/api/client.ts ходит на ${VITE_API_URL}/api/... — в dev это relative /api/
  // через proxy, в prod может быть absolute https://core.pg19.ru/api/. Покрываем оба.
  tracePropagationTargets: [/^\/api\//, apiOrigin],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // v2: Session Replay. Behind feature flag VITE_SENTRY_REPLAY_ENABLED.
    // PII policy (design doc B.1, Premise 4):
    // - maskAllText: true — div/span балансы, greeting с ФИО маскируются
    // - maskAllInputs: true — формы/поиск масируются
    // - blockAllMedia: true — аудио/видео никогда не записываются
    // - networkDetailAllowUrls: [] — RTK Query response body (баланс, счета)
    //   НИКОГДА не попадают в Replay payload
    // - unmask: [] (empty by default) — raise selector в-место только после
    //   доказанной нужды в replay debug (nav badge "Баланс X ₽" = PII leak).
    ...(REPLAY_ENABLED
      ? [
          Sentry.replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
            networkDetailAllowUrls: [],
            networkCaptureBodies: false,
            networkRequestHeaders: [],
            networkResponseHeaders: [],
          }),
        ]
      : []),
  ],
  sendDefaultPii: false,
  beforeSend,
  // v2: sample rates для Session Replay.
  // - 0.05 baseline сессий (5%) — catch silent failures + "SDK didn't load" кейсы
  //   когда нет throw, только Webvisor может помочь (Open Q5).
  // - 1.0 on-error — 100% ошибок получают Replay (main value prop для support UI).
  // Pre-deploy staging measurement: если >50GB/month → cut baseline 0.05 → 0.01.
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAY_SESSION_RATE ?? '0.05'),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAY_ERROR_RATE ?? '1.0'),
})

// v2: session_id tag устанавливается сразу после Sentry.init().
// Support UI имеет второй tab "По сессии" — клиент копирует id с error screen.
Sentry.setTag('session_id', sessionId)

export { Sentry, sessionId }
